import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { politicalMatchProcessingMessages } from '@/data/statusMessages';
import { usePoliticalMatchFlow } from '@/hooks/usePoliticalMatchFlow';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { hashText } from '@/utils/text';

import styles from '@/styles/positions.module.css';

export function PositionsProcessingPage(): JSX.Element {
  const navigate = useNavigate();
  const { state, questionPlan, setPhase, status, error } = usePoliticalMatchFlow();

  useSeo({
    title: buildPageTitle('Processando resultado do Match Político'),
    description: 'Estamos calculando seu posicionamento com base nas respostas enviadas.',
    canonicalPath: '/match-politico/processando',
    indexable: false,
  });

  const delay = useMemo(() => {
    return 1000 + (hashText(`${state.mode}-${questionPlan.length}-${state.currentQuestionIndex}`) % 801);
  }, [questionPlan.length, state.currentQuestionIndex, state.mode]);

  useEffect(() => {
    if (state.phase === 'summary' || state.currentQuestionIndex < questionPlan.length) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPhase('summary');
      navigate('/match-politico/resumo', { replace: true });
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [delay, navigate, questionPlan.length, setPhase, state.currentQuestionIndex, state.phase]);

  if (status === 'loading') {
    return <LoadingState title="Carregando processamento" messages={['Preparando o resultado', 'Aguardando suas respostas', 'Montando os candidatos']} />;
  }

  if (status === 'error') {
    return <EmptyState tone="danger" title="Não foi possível carregar o processamento" description={error ?? ''} />;
  }

  if (!state.mode) {
    return <Navigate to="/match-politico" replace />;
  }

  if (state.phase === 'summary') {
    return <Navigate to="/match-politico/resumo" replace />;
  }

  if (state.currentQuestionIndex < questionPlan.length) {
    return <Navigate to="/match-politico/perguntas" replace />;
  }

  return (
    <section className={styles.questionCard}>
      <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Perguntas', to: '/match-politico/perguntas' }, { label: 'Processando' }]} />

      <StepHeader
        kicker="Etapa 3"
        title="Montando seu resultado"
        description="Calculando o posicionamento geral com base nas respostas enviadas."
      />

      <LoadingState title="Preparando seu resultado" messages={politicalMatchProcessingMessages} />
    </section>
  );
}
